
import { toast as sonnerToast } from "sonner";

export type ToastProps = {
  title?: string;
  description?: string;
  action?: React.ReactNode;
  variant?: "default" | "destructive";
  duration?: number; // Add duration property to support timing
};

// Define proper types for promise functionality
type PromiseT<Data> = Promise<Data> | (() => Promise<Data>);
type PromiseData<Data> = {
  loading: string;
  success: string | ((data: Data) => string);
  error: string | ((error: unknown) => string);
  finally?: () => void;
};

// Create a type-safe interface for our toast functions
export interface ToastAPI {
  (props: ToastProps): string | number; // Add default call signature
  success: (title: string, options?: Omit<ToastProps, 'variant'>) => string | number;
  error: (title: string, options?: Omit<ToastProps, 'variant'>) => string | number;
  info: (title: string, options?: Omit<ToastProps, 'variant'>) => string | number;
  warning: (title: string, options?: Omit<ToastProps, 'variant'>) => string | number;
  destructive: (title: string, options?: Omit<ToastProps, 'variant'>) => string | number;
  message: (title: string, options?: Omit<ToastProps, 'variant'>) => string | number;
  promise: <Data>(
    promise: PromiseT<Data>,
    data: PromiseData<Data>
  ) => string | number;
  dismiss: () => void;
  custom: typeof sonnerToast;
}

// Create the base toast function that implements the call signature
const toastFunction = (props: ToastProps): string | number => {
  return sonnerToast(props.title || "", {
    description: props.description,
    action: props.action,
    duration: props.duration,
  });
};

// Create the complete toast object by combining the base function with additional methods
export const toast = Object.assign(
  toastFunction,
  {
    success: (title: string, options?: Omit<ToastProps, 'variant'>) => {
      return sonnerToast.success(title, options);
    },
    error: (title: string, options?: Omit<ToastProps, 'variant'>) => {
      return sonnerToast.error(title, options);
    },
    info: (title: string, options?: Omit<ToastProps, 'variant'>) => {
      return sonnerToast(title, options);
    },
    warning: (title: string, options?: Omit<ToastProps, 'variant'>) => {
      return sonnerToast.warning(title, options);
    },
    destructive: (title: string, options?: Omit<ToastProps, 'variant'>) => {
      return sonnerToast.error(title, options);
    },
    message: (title: string, options?: Omit<ToastProps, 'variant'>) => {
      return sonnerToast(title, options);
    },
    promise: sonnerToast.promise,
    dismiss: sonnerToast.dismiss,
    custom: sonnerToast,
  }
);

export const useToast = () => {
  const toasts = [] as any[];  // Not used with sonner, but keeps API compatibility
  return {
    toast,
    toasts,
    dismiss: sonnerToast.dismiss,
  };
};

export default useToast;
